import React, { useState, useEffect } from 'react';
import '../styles/Requests.css';
import {
  registerCompany, getMyCompanies, uploadContacts, getTopology, getCompanyNotifications,
} from '../api/companies';

// B2B trust-topology console. Companies verified by ABN/ACN upload their OWN contacts;
// a node lights up ONLY when that person registers + consents. We send raw emails once
// (server hashes them, stores no raw non-user PII). Opt-in job-move alerts land here.
function CompanyConsole() {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadCompanies = async () => {
    try {
      const data = await getMyCompanies();
      setCompanies(data.companies || []);
      if (!selected && data.companies?.length) setSelected(data.companies[0]);
    } catch (err) {
      setError(err.message || 'Could not load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCompanies(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="requests-empty">Loading…</div>;

  return (
    <div className="requests-container">
      <div className="requests-head">
        <h3>Company trust topology</h3>
        <p className="requests-sub">
          Map your real relationship graph. Contacts you upload stay private as hashes — a node
          only ever appears when that person joins ConnectMi and consents.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {companies.length > 0 && (
        <div className="company-tabs">
          {companies.map((c) => (
            <button
              key={c.id}
              className={`company-chip ${selected?.id === c.id ? 'active' : ''}`}
              onClick={() => setSelected(c)}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {selected
        ? <CompanyDetail company={selected} onChanged={loadCompanies} />
        : <RegisterCompany onRegistered={(c) => { setCompanies((p) => [c, ...p]); setSelected(c); }} />}

      {selected && (
        <div style={{ marginTop: 28 }}>
          <RegisterCompany
            compact
            onRegistered={(c) => { setCompanies((p) => [c, ...p]); setSelected(c); }}
          />
        </div>
      )}
    </div>
  );
}

function RegisterCompany({ onRegistered, compact }) {
  const [name, setName] = useState('');
  const [idType, setIdType] = useState('abn');
  const [idValue, setIdValue] = useState('');
  const [seats, setSeats] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const feeFor = (n) => (Number(n) < 50 ? '$100/yr' : Number(n) < 500 ? '$500/yr' : '$2,500/yr');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = { name, seats: Number(seats) || 0 };
      payload[idType] = idValue.replace(/\s/g, '');
      const res = await registerCompany(payload);
      onRegistered(res.company);
      setName(''); setIdValue(''); setSeats('');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={compact ? 'company-register compact' : 'company-register'}>
      <div className="requests-head">
        <h3>{compact ? 'Register another company' : 'Register your company'}</h3>
        <p className="requests-sub">ABN or ACN is verified. Fee tier is set by headcount.</p>
      </div>
      <form className="profile-form" onSubmit={submit}>
        <label>Company name
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Acme Pty Ltd" />
        </label>
        <label>Business identifier
          <div className="id-row">
            <select value={idType} onChange={(e) => setIdType(e.target.value)}>
              <option value="abn">ABN</option>
              <option value="acn">ACN</option>
            </select>
            <input
              value={idValue}
              onChange={(e) => setIdValue(e.target.value)}
              required
              placeholder={idType === 'abn' ? '11 digits' : '9 digits'}
            />
          </div>
        </label>
        <label>Headcount
          <input type="number" min="1" value={seats} onChange={(e) => setSeats(e.target.value)} required placeholder="e.g. 120" />
        </label>
        {seats && <div className="fee-hint">Tier fee: <strong>{feeFor(seats)}</strong></div>}
        <button type="submit" className="btn-yes" disabled={saving}>
          {saving ? 'Registering…' : 'Register company'}
        </button>
      </form>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

function CompanyDetail({ company, onChanged }) {
  const [topology, setTopology] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [raw, setRaw] = useState('');
  const [recency, setRecency] = useState('6m');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const [t, n] = await Promise.all([getTopology(company.id), getCompanyNotifications(company.id)]);
      setTopology(t); setNotifications(n.notifications || []);
    } catch (err) {
      setError(err.message || 'Could not load company');
    }
  };

  useEffect(() => { load(); }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    e.preventDefault();
    setNotice(''); setError(''); setBusy(true);
    try {
      const emails = raw.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s.includes('@'));
      if (emails.length === 0) throw new Error('Paste at least one email address.');
      const contacts = emails.map((email) => ({ email, recency }));
      const res = await uploadContacts(company.id, contacts);
      setNotice(`Uploaded ${res.inserted} — ${res.linked} already on ConnectMi (live now), ${res.pending} pending until they join.`);
      setRaw('');
      load(); onChanged && onChanged();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="company-detail">
      <div className="company-meta">
        <span className="tag-work">{company.abn ? `ABN ${company.abn}` : `ACN ${company.acn}`}</span>
        <span className={company.abn_verified ? 'tag-verified' : 'tag-pending'}>
          {company.abn_verified ? 'Verified' : 'Checksum OK — register-check pending'}
        </span>
        <span className="req-meta">Fee: ${company.annual_fee}/yr</span>
      </div>

      <form className="send-form col" onSubmit={upload}>
        <label>Upload your own contacts (emails) — stored only as hashes
          <textarea
            className="contacts-box"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="jane@acme.com, sam@telco.com…"
            rows={4}
          />
        </label>
        <div className="recency-row">
          <span>Confirmed communication within:</span>
          <select value={recency} onChange={(e) => setRecency(e.target.value)}>
            <option value="3m">last 3 months</option>
            <option value="6m">last 6 months</option>
            <option value="12m">last 12 months</option>
          </select>
          <button type="submit" className="btn-yes" disabled={busy}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
      {notice && <div className="send-notice">{notice}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="topology-stats">
        <div className="stat"><div className="stat-n">{topology?.nodes?.length ?? 0}</div><div className="stat-l">live nodes</div></div>
        <div className="stat"><div className="stat-n">{topology?.pending_count ?? 0}</div><div className="stat-l">pending (not yet joined)</div></div>
      </div>

      <div className="requests-head" style={{ marginTop: 18 }}>
        <h3>Live network</h3>
        <p className="requests-sub">Consenting contacts who are on ConnectMi. Their live employer updates automatically.</p>
      </div>
      {!topology || topology.nodes.length === 0 ? (
        <div className="requests-empty">No live nodes yet — they appear as your contacts join and consent.</div>
      ) : (
        <div className="network-list">
          {topology.nodes.map((n) => (
            <div key={n.id} className="network-row">
              <div className="req-avatar work">{(n.live_name || '?')[0].toUpperCase()}</div>
              <div className="network-name">
                <div>{n.live_name}</div>
                <div className="req-meta">{[n.live_employer, n.live_industry].filter(Boolean).join(' · ')}</div>
              </div>
              <span className="req-meta">{n.recency_bucket}</span>
            </div>
          ))}
        </div>
      )}

      <div className="requests-head" style={{ marginTop: 18 }}>
        <h3>Notifications</h3>
        <p className="requests-sub">When a contact joins, or (if they opted in) changes jobs.</p>
      </div>
      {notifications.length === 0 ? (
        <div className="requests-empty">Nothing yet.</div>
      ) : (
        <div className="network-list">
          {notifications.map((nt) => (
            <div key={nt.id} className="notif-row">
              {nt.type === 'node_added'
                ? <span>🟢 <strong>{nt.payload?.name || 'A contact'}</strong> joined ConnectMi and is now a live node.</span>
                : <span>↪️ <strong>{nt.payload?.name || 'A contact'}</strong> moved: {nt.payload?.from} → <strong>{nt.payload?.to}</strong></span>}
              <span className="req-meta">{new Date(nt.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CompanyConsole;
